import { Component, OnInit, ViewChild } from '@angular/core';
import { delay } from '@okta/okta-auth-js';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';

import { LineItemListModel } from '../../models/LineItemListModel';
import { LineItemListService } from '../../services/line-item-list/line-item-list.service';
import { UserService } from '../../services/user/user.service';
import { LineItemListDisplayableColumns } from './line-item-list-columns/line-item-list-columns.module';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';
import { AuditLogService } from '../../services/audit/audit-log.service';
import { UploadFileDialogComponent } from '../dialogs/upload-file-dialog/upload-file-dialog.component';
import { DisplayPageSize } from '../../Xrefs/xref-display/xrefDisplayColumns';
import { Logging } from '../../utils/logging';

@Component({
  selector: 'app-line-item-list',
  templateUrl: './line-item-list.component.html',
  styleUrls: ['./line-item-list.component.scss'],
  standalone: false,
})
export class LineItemListComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  Loading: Boolean;
  Error: Boolean;
  NoData: Boolean;
  LineItemListData: MatTableDataSource<LineItemListModel>;
  displayableColumns: LineItemListDisplayableColumns;
  displayedColumns = [];
  displayPageSize: DisplayPageSize;
  currentFilter = '';
  reloading = false;
  autoReload = false;
  name = '';
  externalUser: boolean = false;
  userType: number;

  pagLILPageSize: number = 25;

  constructor(
    private lineItemService: LineItemListService,
    private dialog: MatDialog,
    private userService: UserService,
    private gaService: GoogleAnalyticsService,
    private auditService: AuditLogService,
  ) {
    userService.isExternal.subscribe(v => {
      this.externalUser = v;
      if (v) {
        this.userType = 1;
      } else this.userType = 99;
    });
  }

  ngOnInit() {
    this.displayPageSize = new DisplayPageSize();
    this.pagLILPageSize =
      this.displayPageSize.loadPageSize() > 0
        ? this.displayPageSize.loadPageSize()
        : 25;
    this.userService.userName.subscribe(value => {
      this.name = value;
    });
    this.displayableColumns = new LineItemListDisplayableColumns();
    this.displayableColumns.loadDisplayableColumns();
    this.displayedColumns = this.displayableColumns.columnDisplayArray();

    this.LineItemListData = new MatTableDataSource();
    this.LineItemListData.paginator = this.paginator;
    this.LineItemListData.sort = this.sort;

    this.loadData();

    this.LineItemListData.filterPredicate = (
      data: LineItemListModel,
      filter: string,
    ) => {
      this.currentFilter = filter;
      if (!filter || filter == '') return true;
      // Create string with all of the data from shown columns for the row.
      // Seperated by a non-typable character (in this case a bullet)
      let sString = '';
      try {
        this.displayableColumns.orderColumnKeys.forEach(key => {
          if (
            this.displayableColumns.columns[key].visible &&
            this.displayableColumns.columns[key].filterable
          ) {
            sString = sString.concat(data[key], String.fromCharCode(149));
          }
        });
      } catch (e) {
        Logging.error('Error in filter');
      }
      // Look for the filter in the concatinated data
      return sString.toLowerCase().includes(filter.toLowerCase());
    };
    // Set the sorts to be case insensitive
    this.LineItemListData.sortingDataAccessor = (item, property) => {
      try {
        if (property == 'lineItemListID' || property == 'noOfRecords') {
          return +item[property];
        }
        if (property == 'batchProcessDate' || property == 'batchUploadDate') {
          // For a date
          if (item[property] == null || item[property] == '') return 0;
          const date = item[property].split('/');
          const num = +date[2] * 10000 + +date[0] * 100 + +date[1];
          return num;
        }
        return item[property].trim().toLowerCase();
      } catch (e) {
        return item[property];
      }
    };
  }

  public loadData() {
    this.Loading = true;
    this.Error = false;
    this.NoData = false;
    let foundOne = false;

    this.lineItemService
      .getLineItemList()
      .then(response => {
        this.LineItemListData.data = response.filter(l => {
          return l.userID == this.name;
        });
        this.Error = false;
        this.Loading = false;
        this.reloading = false;
        response.forEach(l => {
          if (
            l.statusDesc == 'File Loaded' ||
            l.statusDesc == 'Send to Product Identificaiton'
          ) {
            foundOne = true;
          }
        });
        this.autoReload = foundOne;
      })
      .catch(error => {
        this.Loading = false;
        this.Error = true;
        Logging.error(error);
      });
  }

  public showDownloadButton(status: string): boolean {
    return status == 'Process Completed';
  }

  public downloadFile(
    userName: string,
    fileName: string,
    id: number,
    reportType: string,
  ) {
    /*added to capture GA export event*/
    this.gaService.exportEvent('LIU', reportType);
    this.auditService.exportEvent('LIU', reportType);

    const fileLoc =
      '/api/LineItem/DownloadExcel?userName=' +
      userName +
      '&fileName=' +
      fileName +
      '&batchId=' +
      id +
      '&downloadType=' +
      reportType;
    this.lineItemService.downloadFile(fileLoc);
  }

  delayedReloadData() {
    this.autoReload = true;
    (async () => {
      let count = 0;
      while (this.autoReload && count < 7) {
        count++;
        this.loadData();
        await delay(10000);
      }
      this.autoReload = false;
      this.Loading = false;
    })();
  }

  public getColumnHeaderName(key: string) {
    let header: string;
    try {
      header = this.displayableColumns.columns[key].columnHeaderName;
    } catch (e) {
      header = 'Unknown Column';
    }
    return header;
  }

  ApplyFilter(filterValue: string) {
    this.LineItemListData.filter = filterValue.trim().toLowerCase();
    if (this.LineItemListData.paginator) {
      this.LineItemListData.paginator.firstPage();
    }
  }

  private readAndUploadFile(theFile: any) {
    const fileData = new FormData();
    fileData.append('file', theFile, theFile.name);
    // TODO why are we subscribing again?
    // This code will be called if the username is changed for any reason
    // Is that what we want?
    this.userService.userName.subscribe(value => {
      fileData.append('user', value);
      this.reloading = true;
      this.lineItemService.postFile(fileData);
      this.gaService.uploadEvent();
      this.auditService.uploadEvent();
    });
    this.delayedReloadData();
  }

  showUploadFileDialog(): void {
    const dialogRef = this.dialog.open(UploadFileDialogComponent, {
      width: '300px',
      data: null,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.readAndUploadFile(result);
      }
    });
  }

  showIndividualErrorButton(error: number): boolean {
    return error == 1;
  }

  downloadIndividualErrors(id: number) {
    const fileLoc = '/api/LineItem/DownloadErrorsExcel?batchId=' + id;
    this.lineItemService.downloadFile(fileLoc);
  }

  changePageSize(pageSize: number) {
    this.displayPageSize.savePage(pageSize);
  }
}
